<template>
  <div>
    <header class="wsp-toolbar">
      <div class="wsp-toolbar__title">
        <span class="wsp-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Videos</h1>
          <p>YouTube videos featured on the website</p>
        </div>
      </div>
      <div class="wsp-toolbar__actions">
        <div class="wsp-search">
          <span class="wsp-search__icon">🔍</span>
          <input v-model.trim="search" type="text" placeholder="Search videos…" />
        </div>
        <button class="wsp-btn-refresh" :class="{ 'is-loading': loading }" :disabled="loading" @click="fetch">
          <svg class="wsp-btn-refresh__icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ loading ? 'Refreshing…' : 'Refresh' }}</span>
        </button>
        <button class="wsp-btn-primary" @click="openCreate">+ Add Video</button>
      </div>
    </header>

    <CAlert v-if="error" color="danger" show>{{ error }}</CAlert>

    <section class="wsp-card">
      <header class="wsp-card__head">
        <h2>All Videos</h2>
        <span class="wsp-card__count">{{ filtered.length }} video(s)</span>
      </header>

      <div v-if="loading" class="wsp-loading"><CSpinner size="sm" /> Loading…</div>
      <div v-else-if="!filtered.length" class="wsp-empty">
        <div class="wsp-empty__title">No videos found</div>
        <div>Add a YouTube video to feature on the site.</div>
      </div>
      <div v-else>
        <table class="wsp-table">
          <thead>
            <tr>
              <th style="width:50px;">Letter</th>
              <th>Label</th>
              <th>Video ID</th>
              <th>Position</th>
              <th>Featured</th>
              <th class="wsp-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filtered" :key="row.id">
              <td>
                <span class="ws-letter-badge">{{ (row.letter || '?').slice(0,1).toUpperCase() }}</span>
              </td>
              <td><strong>{{ row.label }}</strong></td>
              <td><code style="font-size:0.75rem;color:#0e5b46;">{{ row.video_id }}</code></td>
              <td>{{ row.position == null ? '—' : row.position }}</td>
              <td>
                <span v-if="row.is_featured" class="wsp-pill wsp-pill--amber">★ Featured</span>
                <span v-else style="color:#9ca3af;">—</span>
              </td>
              <td class="wsp-actions">
                <button class="wsp-icon-btn" @click="openEdit(row)" title="Edit">✎</button>
                <button class="wsp-icon-btn wsp-icon-btn--danger" @click="confirmDelete(row)" title="Delete">🗑</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div v-if="modalOpen" class="wsp-modal-backdrop" @click.self="closeModal">
      <div class="wsp-modal">
        <header class="wsp-modal__head">
          <h3>{{ form.id ? 'Edit Video' : 'Add Video' }}</h3>
          <button class="wsp-modal__close" @click="closeModal">×</button>
        </header>
        <div class="wsp-modal__body">
          <div class="wsp-form-grid">
            <div class="wsp-field">
              <label>Label <span style="color:#b91c1c">*</span></label>
              <input v-model="form.label" type="text" maxlength="100" />
              <span v-if="errors.label" class="wsp-field__error">{{ errors.label }}</span>
            </div>
            <div class="wsp-field">
              <label>Letter <span style="color:#b91c1c">*</span></label>
              <input v-model="form.letter" type="text" maxlength="1" placeholder="A" />
              <span v-if="errors.letter" class="wsp-field__error">{{ errors.letter }}</span>
            </div>
            <div class="wsp-field wsp-field--full">
              <label>YouTube Video ID <span style="color:#b91c1c">*</span></label>
              <input v-model="form.video_id" type="text" maxlength="50" placeholder="dQw4w9WgXcQ" />
              <span class="wsp-field__hint">Just the ID, not the full URL.</span>
              <span v-if="errors.video_id" class="wsp-field__error">{{ errors.video_id }}</span>
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Cover Image URL</label>
              <input v-model="form.cover_image" type="text" placeholder="https://…" />
              <img v-if="form.cover_image" :src="form.cover_image" alt="" style="margin-top:8px;width:160px;border-radius:8px;border:1px solid #e5e9f2;" />
            </div>
            <div class="wsp-field">
              <label>Position</label>
              <input v-model.number="form.position" type="number" />
            </div>
            <div class="wsp-field">
              <label>&nbsp;</label>
              <label class="wsp-toggle">
                <input v-model="form.is_featured" type="checkbox" /> Featured (only one allowed)
              </label>
            </div>
          </div>
        </div>
        <footer class="wsp-modal__foot">
          <button class="wsp-btn-ghost" :disabled="saving" @click="closeModal">Cancel</button>
          <button class="wsp-btn-primary" :disabled="saving" @click="save">
            {{ saving ? 'Saving…' : (form.id ? 'Save changes' : 'Create video') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import websitesApi from '@/api/websites'

const empty = () => ({ id: null, label: '', letter: '', video_id: '', cover_image: '', is_featured: false, position: null })

export default {
  name: 'WebsitesVideos',
  data () {
    return {
      rows: [], loading: false, error: '',
      search: '',
      modalOpen: false, saving: false,
      form: empty(), errors: {},
    }
  },
  computed: {
    filtered () {
      const q = this.search.toLowerCase()
      if (!q) return this.rows
      return this.rows.filter(r =>
        (r.label || '').toLowerCase().includes(q) ||
        (r.video_id || '').toLowerCase().includes(q)
      )
    },
  },
  mounted () { this.fetch() },
  methods: {
    async fetch () {
      this.loading = true; this.error = ''
      try { this.rows = await websitesApi.videosList() }
      catch (e) { this.error = this.extractError(e) }
      finally { this.loading = false }
    },
    openCreate () { this.form = empty(); this.errors = {}; this.modalOpen = true },
    openEdit (row) {
      this.form = {
        id: row.id,
        label: row.label || '',
        letter: row.letter || '',
        video_id: row.video_id || '',
        cover_image: row.cover_image || '',
        is_featured: !!row.is_featured,
        position: row.position == null ? null : Number(row.position),
      }
      this.errors = {}; this.modalOpen = true
    },
    closeModal () { if (!this.saving) this.modalOpen = false },
    async save () {
      this.errors = {}
      if (!this.form.label.trim())    this.errors.label    = 'Required'
      if (!this.form.letter.trim())   this.errors.letter   = 'Required'
      if (!this.form.video_id.trim()) this.errors.video_id = 'Required'
      if (Object.keys(this.errors).length) return
      this.saving = true
      try {
        const payload = {
          label: this.form.label,
          letter: this.form.letter,
          video_id: this.form.video_id,
          cover_image: this.form.cover_image || null,
          is_featured: !!this.form.is_featured,
          position: this.form.position == null || this.form.position === '' ? null : Number(this.form.position),
        }
        if (this.form.id) await websitesApi.videoUpdate(this.form.id, payload)
        else              await websitesApi.videoCreate(payload)
        this.modalOpen = false
        await this.fetch()
      } catch (e) { this.error = this.extractError(e) }
      finally { this.saving = false }
    },
    async confirmDelete (row) {
      if (!confirm(`Delete video "${row.label}"?`)) return
      try { await websitesApi.videoDelete(row.id); await this.fetch() }
      catch (e) { this.error = this.extractError(e) }
    },
    extractError (e) {
      return (e && e.response && e.response.data && (e.response.data.error || e.response.data.message))
        || (e && e.message) || 'Request failed'
    },
  },
}
</script>

<style scoped>
.ws-letter-badge {
  display: inline-flex;
  align-items: center; justify-content: center;
  width: 36px; height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #fff; font-weight: 700; font-size: 1rem;
}
</style>
